import {
  keepPreviousData,
  QueryClient,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { SDKError } from '@crossnokaye/typescript-sdk/models/errors';

export interface WebsocketSubscription {
  subscribed: boolean;
}

export interface UseWebsocketQueryOptions<T, R = T>
  extends Omit<UseQueryOptions<T, SDKError, R>, 'queryFn'> {
  webSocketUrl: string;
}

export const useWebsocketQuery = <T, R = T>(
  queryOptions: UseWebsocketQueryOptions<T | null, R>
): UseQueryResult<R | null> => {
  const { webSocketUrl, ...rest } = queryOptions;
  return useQuery({
    ...rest,
    queryFn: () => Promise.resolve(null),
    placeholderData: keepPreviousData,
  });
};

interface Subscription {
  newSubscriptions: string[][];
  updated: boolean;
}

/**
 * Check if the new subscription keys are already in the existing subscriptions
 * Subscription state is stored in the query data under the key 'subscribed'
 * @param queryClient The query client
 * @param queryKey The query key
 * @param newSubscriptionKeys New query keys to subscribe to
 * @returns The complete set of query keys to subscribe to and if the subscriptions have been updated
 */
export const checkSubscription = (
  queryClient: QueryClient,
  queryKey: string[],
  newSubscriptionKeys: string[][]
): Subscription => {
  const existingSubscriptions = queryClient
    .getQueryCache()
    .findAll({
      queryKey: queryKey,
    })
    .filter((query) => (query.state.data as WebsocketSubscription)?.subscribed)
    .map((query) => query.queryKey as string[]);

  // Remove duplicates
  const seen = new Set<string>();
  const newSubscriptions: string[][] = [];

  for (const subscription of [...existingSubscriptions, ...newSubscriptionKeys]) {
    const key = JSON.stringify(subscription);
    if (!seen.has(key)) {
      seen.add(key);
      newSubscriptions.push(subscription);
    }
  }

  return {
    newSubscriptions,
    updated: newSubscriptions.length > existingSubscriptions.length,
  };
};
